import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { apiRequest } from '../utils/api';

const EarningsUpdate = () => {
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const handleUpdate = async () => {
    setLoading(true);
    setLogs([]);
    setError(null);
    
    try {
      addLog('Starting earnings calendar update...');
      addLog(`Start date: ${startDate}`);
      addLog(`Number of days: ${days}`);

      const response = await apiRequest('/api/earnings/update', {
        method: 'POST',
        body: JSON.stringify({
          startDate,
          days
        })
      });

      if (response.logs && Array.isArray(response.logs)) {
        response.logs.forEach(log => addLog(log));
      }

      addLog('Update completed successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error occurred';
      setError(errorMessage);
      addLog(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 1, sm: 4 }, mb: 4, px: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="body1" color="text.secondary" paragraph>
          Update the earnings calendar data from the earnings API.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 4 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={loading}
            />
          </Box>
          <Box sx={{ width: 150 }}>
            <TextField
              fullWidth
              label="Days"
              type="number"
              value={days}
              onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
              disabled={loading}
              inputProps={{ min: 1 }}
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Update Log
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                maxHeight: 400,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              {logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No logs yet. Click "Update" to start the earnings calendar update.
                </Typography>
              ) : (
                logs.map((log, index) => (
                  <Box key={index} sx={{ mb: 0.5 }}>
                    {log}
                  </Box>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default EarningsUpdate;
